import React, { useState, useMemo, useEffect } from 'react';
import { filterRecordsForTableSearch } from '../utils/tableRecordSearch';
import {
  ALWAYS_INCLUDE_REFERRAL_DATA_FIELDS,
  isReferralRawDataType,
} from '../utils/referralRawColumns';
import './DataTable.css';

const ROWS_PER_PAGE = 200;

function isDateColumn(column) {
  if (!column) return false;
  if (column === 'timestamp') return true;
  const key = column.includes('.') ? column.split('.').pop() : column;
  return /date|time/i.test(key);
}

function isNumericColumn(column) {
  if (!column) return false;
  if (column === 'id' || column === 'timestamp') return true;
  const key = column.includes('.') ? column.split('.').pop() : column;
  return /_id$|^id$|_count$|_number$|_no$|num_|^num/i.test(key);
}

function defaultSortDir(column) {
  return isDateColumn(column) || isNumericColumn(column) ? 'desc' : 'asc';
}

function resolveDefaultSortField(columns) {
  if (columns.includes('timestamp')) return 'timestamp';
  if (columns.includes('data.created_date')) return 'data.created_date';
  const dateCol = columns.find(isDateColumn);
  if (dateCol) return dateCol;
  return columns[0] || 'timestamp';
}

function getNestedValue(obj, path) {
  if (!path.includes('.')) {
    return obj[path];
  }

  const parts = path.split('.');
  let value = obj;

  for (const part of parts) {
    if (value && typeof value === 'object') {
      value = value[part];
    } else {
      return undefined;
    }
  }

  return value;
}

function valueForSort(record, column) {
  const value = getNestedValue(record, column);
  if (value === null || value === undefined || value === '') return null;
  if (isDateColumn(column)) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value).toLowerCase() : d.getTime();
  }
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  const asNum = Number(value);
  if (Number.isFinite(asNum) && /^-?\d+(\.\d+)?$/.test(String(value).trim())) {
    return asNum;
  }
  return String(value).toLowerCase();
}

function SortableTh({ field, label, sortField, sortDir, onSort }) {
  const active = sortField === field;
  const indicator = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th
      className={`sortable ${active ? 'sorted' : ''}`}
      onClick={() => onSort(field)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSort(field);
        }
      }}
      tabIndex={0}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      title="Click to sort"
    >
      {label}{indicator}
    </th>
  );
}

function DataTable({ data, dataType, searchTerm = '', onSearchChange }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('timestamp');
  const [sortDir, setSortDir] = useState('desc');

  const searchedData = useMemo(
    () => filterRecordsForTableSearch(data, dataType, searchTerm),
    [data, dataType, searchTerm],
  );

  // Get all unique columns from the data
  const columns = useMemo(() => {
    if (!data || data.length === 0) return [];

    const allKeys = new Set();

    data.forEach(record => {
      // Add top-level keys
      Object.keys(record).forEach(key => allKeys.add(key));

      // Add nested data keys
      if (record.data && typeof record.data === 'object') {
        Object.keys(record.data).forEach(key => allKeys.add(`data.${key}`));
      }

      // Add nested metadata keys
      if (record.metadata && typeof record.metadata === 'object') {
        Object.keys(record.metadata).forEach(key => allKeys.add(`metadata.${key}`));
      }
    });

    // Always show non-VE flags on referral raw views, even before the API
    // populates them (cells stay blank until a cache refresh picks them up).
    if (isReferralRawDataType(dataType)) {
      ALWAYS_INCLUDE_REFERRAL_DATA_FIELDS.forEach(f => allKeys.add(`data.${f}`));
    }

    return Array.from(allKeys).sort();
  }, [data, dataType]);

  useEffect(() => {
    const field = resolveDefaultSortField(columns);
    setCurrentPage(1);
    setSortField(field);
    setSortDir(defaultSortDir(field));
  }, [data, dataType, columns]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(defaultSortDir(field));
    }
    setCurrentPage(1);
  };

  // Format value for display
  const formatValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const sortedData = useMemo(() => {
    if (!searchedData || searchedData.length === 0) return [];
    const dir = sortDir === 'asc' ? 1 : -1;
    const out = [...searchedData];
    out.sort((a, b) => {
      const av = valueForSort(a, sortField);
      const bv = valueForSort(b, sortField);
      const aEmpty = av == null;
      const bEmpty = bv == null;
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
    return out;
  }, [searchedData, sortField, sortDir]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedData.length / ROWS_PER_PAGE);
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;
  const currentData = sortedData.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxButtons = 10;

    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    // First page
    if (startPage > 1) {
      buttons.push(
        <button key="first" onClick={() => goToPage(1)} className="page-btn">
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(<span key="ellipsis1" className="ellipsis">...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => goToPage(i)}
          className={`page-btn ${i === currentPage ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="ellipsis2" className="ellipsis">...</span>);
      }
      buttons.push(
        <button key="last" onClick={() => goToPage(totalPages)} className="page-btn">
          {totalPages}
        </button>
      );
    }

    return buttons;
  };

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className="data-table-container">
      <div className="table-header">
        <h3>Raw Data</h3>
        <div className="table-info">
          Showing {sortedData.length === 0 ? 0 : startIndex + 1} - {Math.min(endIndex, sortedData.length)} of {sortedData.length} records
          {searchTerm.trim() && data.length !== sortedData.length && (
            <span className="table-info-filtered"> (filtered from {data.length})</span>
          )}
        </div>
      </div>
      {onSearchChange && (
        <div className="table-toolbar">
          <input
            type="search"
            className="table-search"
            placeholder="Search all fields…"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search raw records"
          />
        </div>
      )}

      <div className="table-wrapper">
        {sortedData.length === 0 ? (
          <div className="table-search-empty">No matching records.</div>
        ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th className="row-number">#</th>
              {columns.map(column => (
                <SortableTh
                  key={column}
                  field={column}
                  label={column}
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.map((record, index) => (
              <tr key={record.id || startIndex + index}>
                <td className="row-number">{startIndex + index + 1}</td>
                {columns.map(column => (
                  <td key={column}>
                    {formatValue(getNestedValue(record, column))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="page-btn nav-btn"
          >
            Previous
          </button>

          {renderPaginationButtons()}

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="page-btn nav-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default DataTable;
